import { ReactNode } from 'react'
import cn from 'classnames'
import Tooltip from 'core/components/dialogs/Tooltip'
import Status from 'core/widgets/status/Status'
import {
  ComplianceMileageRequirementStatus,
  ComplianceVerificationStatus
} from 'types/entity'

interface ComplianceStatusProps {
  status:
    | ComplianceVerificationStatus
    | 'needs_review'
    | null
    | undefined
    | ComplianceMileageRequirementStatus
  reasons?: string[]
  title?: string
  footer?: ReactNode
}

const statusMap = {
  unsubmitted: {
    textColor: '333333',
    bgColor: 'ebebeb',
    status: 'Not Submitted'
  },
  pending_review: {
    textColor: 'f17200',
    bgColor: 'fff1d9',
    status: 'Pending Review'
  },
  needs_review: {
    textColor: 'f17200',
    bgColor: 'fff1d9',
    status: 'Needs Review'
  },
  in_review: {
    textColor: 'f17200',
    bgColor: 'fffbda',
    status: 'In Review'
  },
  rejected: {
    textColor: 'eb001c',
    bgColor: 'fff1f1',
    status: 'Not Compliant'
  },
  approved: {
    textColor: '31b59f',
    bgColor: 'eafffa',
    status: 'Compliant'
  },
  below_requirement: {
    textColor: 'eb001c',
    bgColor: 'fff1f1',
    status: 'Below Requirement'
  },
  meets_requirement: {
    textColor: '31b59f',
    bgColor: 'eafffa',
    status: 'Meets Requirement'
  }
}

const ComplianceStatus = ({
  status,
  reasons = [],
  title,
  footer
}: ComplianceStatusProps) => {
  if (status === null) {
    return <span>N/A</span>
  }
  if (status === undefined || !statusMap[status]) {
    return null
  }
  const disabled =
    reasons.length === 0 || !['rejected', 'below_requirement'].includes(status)

  return (
    <Tooltip
      dark
      wrapperClassName={cn('truncate', disabled ? '' : 'cursor-pointer')}
      disabled={disabled}
      content={
        <div
          className={cn(
            'flex flex-col gap-2',
            status === 'below_requirement' ? 'max-w-96' : 'max-w-72'
          )}
        >
          {title && <p>{title}</p>}
          <ul
            className={cn(
              'grid',
              status === 'below_requirement' ? 'gap-1' : 'list-disc pl-6 gap-2'
            )}
          >
            {reasons?.map((reason, i) => {
              return (
                <li key={`rejection-reason-${i}`}>
                  <span className="whitespace-normal">{reason}</span>
                </li>
              )
            })}
          </ul>
          {footer}
        </div>
      }
    >
      <Status
        textColor={statusMap[status].textColor}
        bgColor={statusMap[status].bgColor}
        status={statusMap[status].status}
      />
    </Tooltip>
  )
}

export default ComplianceStatus
